/**
 * Formats a given date to a specific time format based on the provided locale.
 *
 * @param date - The date to be formatted.
 * @param locale - The locale string to use for formatting (e.g., "it-IT", "en-US").
 * @param timeZone - Optional IANA zone. Omitted means the device's own zone,
 *   which every on-screen caller wants. The chat export passes the order's.
 * @returns The formatted time as a string.
 *
 * @example
 * const date = new Date("2025-01-27T14:30:00");
 * console.log(formatTime(date, "it-IT")); // Output: "14:30" (example output)
 * console.log(formatTime(date, "en-US")); // Output: "2:30 PM" (example output)
 */
export const formatTime = (
  date: Date,
  locale = "en-GB",
  timeZone?: string,
): string => {
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    // An explicitly-undefined `timeZone` is the same as an absent one to
    // `Intl.DateTimeFormat` (ECMA-402 InitializeDateTimeFormat falls back to
    // DefaultTimeZone), so on-screen behaviour is byte-for-byte unchanged.
    timeZone,
  }).format(date);
};

/**
 * Formats a Date object into a locale-sensitive string containing the full date.
 *
 * @param date - The Date object to format.
 * @param locale - The locale string (e.g., "en-GB", "en-US"). Defaults to "en-GB".
 * @param timeZone - Optional IANA zone (see `formatTime`). It moves the
 *   calendar DAY, not only the clock: 23:30 UTC is tomorrow in Rome.
 * @returns A formatted string representing the full date (e.g., "27/01/2025").
 *
 * @example
 * const date = new Date("2025-01-27");
 * console.log(formatDate(date, "en-GB")); // Output: "27/01/2025" (example output)
 * console.log(formatDate(date, "en-US")); // Output: "01/27/2025" (example output)
 */
export const formatDate = (
  date: Date,
  locale = "en-GB",
  timeZone?: string,
): string => {
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  }).format(date);
};

/**
 * The day and the short month, as a chip prints it: "18 Mar" (en-GB), "18 mar" (it-IT).
 *
 * @param date - The Date object to format.
 * @param locale - The locale string. Defaults to "en-GB".
 * @param timeZone - Optional IANA zone (see `formatTime`); it moves the calendar day.
 * @returns The localized day and short month.
 */
export const formatShortDayMonth = (
  date: Date,
  locale = "en-GB",
  timeZone?: string,
): string => {
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    timeZone,
  }).format(date);
};

/**
 * Formats a Date object into a colloquial date string based on the provided locale.
 *
 * @param date - The Date object to format.
 * @param locale - A string representing the locale, e.g., "en-GB" or "it-IT". Defaults to "en-GB".
 * @returns A formatted date string in a colloquial format.
 *
 * @example
 * const date = new Date("2025-01-27");
 * console.log(formatFullDate(date, "en-GB")); // Output: "Monday, 27 January 2025" (example output)
 * console.log(formatFullDate(date, "it-IT")); // Output: "lunedì, 27 gennaio 2025" (example output)
 */
export const formatFullDate = (date: Date, locale = "en-GB"): string => {
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
};

/**
 * Formats a Date object into a locale-sensitive string with a weekday and short year format.
 *
 * @param date - The Date object to format.
 * @param locale - A string representing the locale, e.g., "en-GB" or "it-IT". Defaults to "en-GB".
 * @param timeZone - Optional IANA zone (see `formatTime`). It moves the
 *   calendar DAY, not only the weekday name: 23:30 UTC is tomorrow in Rome.
 * @returns A formatted date string including the weekday, year (2-digit), month, and day.
 *
 * @example
 * const date = new Date("2025-01-27");
 * console.log(formatShortDateWithWeekday(date, "en-GB")); // Output: "Monday, 27/01/25" (example output)
 * console.log(formatShortDateWithWeekday(date, "it-IT")); // Output: "lunedì, 27/01/25" (example output)
 */
export const formatShortDateWithWeekday = (
  date: Date,
  locale = "en-GB",
  timeZone?: string,
): string => {
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  }).format(date);
};

/**
 * Parses a "YYYY-MM-DD" civil-day key (the GraphQL `Date` scalar wire format,
 * e.g. `OrderItemSchedule.date`) into a local-midnight Date — no UTC drift:
 * `new Date("2026-08-01")` would parse as UTC midnight and render as the
 * previous day in western timezones.
 *
 * @param key - The date key to parse, e.g. "2026-08-01".
 * @returns A Date at local midnight of that civil day, or null when the key
 *          does not match the expected shape.
 *
 * @example
 * dateKeyToLocalDate("2026-08-01"); // Aug 1st 2026, 00:00 local time
 * dateKeyToLocalDate("not-a-date"); // null
 */
export const dateKeyToLocalDate = (key: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  // Reject keys that only look valid (e.g. "2026-13-45" rolls over).
  if (
    date.getFullYear() !== Number(year) ||
    date.getMonth() !== Number(month) - 1 ||
    date.getDate() !== Number(day)
  ) {
    return null;
  }
  return date;
};

/**
 * Whether a string LOOKS like a civil-day key. Shape only —
 * `dateKeyToLocalDate` above owns validity, and "2026-13-45" passes here.
 * Callers use it to tell a refused day key apart from a full ISO datetime,
 * which `new Date()` reads correctly and must still be formatted.
 *
 * Month and day are 1-OR-2 digits, wider than the parse on purpose:
 * padded-only left the roll-over one keystroke away, since "2026-2-30" would
 * miss this branch and print 02/03/2026. It lives beside the parse so the two
 * regexes cannot drift apart in separate packages (EAH-2065).
 *
 * @param value - The string to test, e.g. "2026-08-01".
 * @returns True when it is day-shaped, real day or not.
 */
export const isDateKeyShaped = (value: string): boolean =>
  /^\d{4}-\d{1,2}-\d{1,2}$/.test(value);

/**
 * The one message both apps log when a booked day key is not a real day.
 * Shared so a single Sentry search answers "has this ever fired anywhere" —
 * the reachability claim in EAH-2065 rests on that search staying empty.
 * Log it with `{ site, date }` so the two call sites stay tellable apart.
 */
export const UNUSABLE_BOOKED_DAY_KEY = "Order chat: unusable booked day key";
